<script setup lang="ts">
import { useGameWinnerTile, useThemeLogo, useI18n, ref } from '#imports'

const { t } = useI18n()
const brandIcon = useThemeLogo('icon')

const { winner, icon, money, wonTimeAgo, isMegaPrize, startGame } = useGameWinnerTile()

const useFallbackLogo = ref(false)
</script>

<template>
    <div
        class="winner-tile-short"
        @click="startGame()"
    >
        <div class="winner-tile-short__image">
            <NuxtImg
                v-if="(useFallbackLogo || !icon?.trim()) && brandIcon"
                provider="cloudflare"
                preset="local"
                :src="brandIcon.src"
                :quality="brandIcon.quality"
                width="42"
                height="42"
                alt=""
                loading="lazy"
                decoding="async"
            />
            <NuxtImg
                v-else
                provider="cloudflare"
                preset="gameservice"
                loading="lazy"
                decoding="async"
                :src="icon"
                :alt="winner.gameName"
                :title="winner.gameName"
                width="42"
                height="42"
                quality="70"
                @error="useFallbackLogo = true"
            />
        </div>

        <div class="winner-tile-short__user">
            <div
                class="winner-tile-short__username"
                :title="winner.wonBy || winner.username"
            >
                {{ winner.wonBy || winner.username }}
            </div>
        </div>

        <div class="winner-tile-short__amount">
            {{ money }}
        </div>

        <div v-if="isMegaPrize" class="winner-tile-short__gamename">
            <div>
                {{ t('game.gameWinnerTile.mega_prize') }}
            </div>
        </div>

        <p v-else class="winner-tile-short__game-name">
            {{ winner.gameName }}
        </p>

        <Transition name="fade" appear>
            <p v-if="wonTimeAgo" class="winner-tile-short__time">
                {{ wonTimeAgo }}
            </p>
        </Transition>
    </div>
</template>

<style scoped lang="scss">
.winner-tile-short {
    display: grid;
    cursor: pointer;
    grid-template-columns: max-content 1fr auto;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
        'image user amount'
        'image gamename gamename';
    grid-column-gap: 8px;

    * {
        margin: 0;
        padding: 0;
    }

    &__image {
        display: flex;
        grid-area: image;
        justify-content: flex-start;
        align-items: center;

        img {
            min-width: 42px;
            max-width: 42px;
            min-height: 42px;
            max-height: 42px;
            border-radius: var(--border-radius, $border-radius);
        }
    }

    &__user {
        display: flex;
        grid-area: user;
        align-self: flex-end;
        min-width: 0;
        max-width: 100%;
    }

    &__username {
        width: auto;
        margin: 0 2px 0 0;
        overflow: hidden;
        font-weight: $text-bold;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__amount {
        grid-area: amount;
        align-self: flex-end;
        font-weight: $text-bold;
        text-align: right;
    }

    &__game-name {
        grid-area: gamename;
        align-self: flex-start;
        width: auto;
        overflow: hidden;
        font-size: 10px;
        white-space: nowrap;
        text-overflow: ellipsis;

        @media (min-width: $screen-xl) {
            font-size: $font-size-xs;
        }
    }

    &__time {
        position: absolute;
        right: 0;
        bottom: 0;
        padding: 1px 5px;
        border-radius: 6px 0;
        opacity: 0.7;
        background-color: rgb(0 0 0 / 10%);
        font-size: 10px;
        min-height: 16px;
    }

    .winner-tile--mega-prize & {
        grid-template-areas:
            'image user gamename'
            'image amount time';

        &__gamename {
            overflow: visible;

            div {
                margin-top: -8px;
                padding: 1px 4px;
                float: right;
                border-radius: 0 0 6px 6px;
                color: $bingo-purple;
                background: $gold;
                font-size: 9px;
                font-weight: $text-heavy;
            }
        }

        &__amount {
            align-self: flex-start;
            color: $gold;
            font-size: $font-size-base;
            font-weight: $text-heavy;
            line-height: 1;
            text-align: left;
        }
    }

    .winner-tile--big-win & {
        &__amount {
            color: var(
                --game-winner-big-win-amount-color,
                var(--game-winner-latest-amount-color, $game-winner-latest-amount-color)
            );
            font-size: 16px;
            font-weight: $text-extra-bold;
            line-height: 1;

            @include tablet {
                font-size: 18px;
            }
        }
    }

    .winner-tile--horizontal & {
        &__time {
            display: none;
        }

        &__amount {
            color: var(--game-winner-latest-amount-color, $game-winner-latest-amount-color);
        }
    }

    .winners-sidebar--horizontal .winner-tile--big-win & {
        grid-template-areas:
            'image user amount'
            'image gamename amount';

        &__time {
            display: none;
        }
    }
}
</style>
